import { useCallback, useEffect, useState } from "react";
import { VISOR_MODULE_BREADCRUMB_ROOT } from "@/modules/workModuleCore";
import { useNavigate } from "react-router-dom";
import api, { formatApiError, fmtMoney } from "@/lib/api";
import { usePersistedClientId } from "@/lib/clientSelection";
import { PageHeader } from "./layoutChrome";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  ShieldAlert,
  RefreshCw,
  Loader2,
  Search,
  ExternalLink,
  Upload,
  AlertTriangle,
  Info,
  CheckCircle2,
  XCircle,
} from "lucide-react";
import { toast } from "sonner";
import { goToSatVisorForUuid } from "@/lib/satVisorNavigation";

const SITUACION_LABEL = {
  presunto: "Presunto",
  definitivo: "Definitivo",
  desvirtuado: "Desvirtuado",
  sentencia_favorable: "Sentencia favorable",
};

const SITUACION_SECCION = {
  presunto: "Presuntos",
  definitivo: "Definitivos",
  desvirtuado: "Desvirtuados",
  sentencia_favorable: "Sentencia favorable",
};

export function normalizeConsultaRfc(raw) {
  return String(raw || "")
    .trim()
    .toUpperCase()
    .replace(/[\s-]/g, "");
}

/** Normaliza texto de búsqueda (RFC o nombre de empresa). */
export function normalizeConsultaEfosQuery(raw) {
  const s = String(raw || "").trim();
  // Si parece RFC corto (sin espacios raros), normalizar como RFC
  const compact = normalizeConsultaRfc(s);
  if (/^[A-ZÑ&]{3,4}\d{6}[A-Z0-9]{3}$/.test(compact) || (compact.length >= 12 && compact.length <= 13 && !/\s/.test(s.trim()))) {
    return compact;
  }
  return s.replace(/\s+/g, " ");
}

function seccionListaLabel(result) {
  if (!result) return "";
  if (result.seccion_lista) return result.seccion_lista;
  const norm = result.situacion_norm;
  return SITUACION_SECCION[norm] || SITUACION_LABEL[norm] || result.situacion || "";
}

function situacionBadgeClass(norm) {
  if (norm === "definitivo") return "bg-red-100 text-red-900 border-red-200";
  if (norm === "presunto") return "bg-amber-100 text-amber-950 border-amber-200";
  if (norm === "desvirtuado") return "bg-slate-100 text-slate-700 border-slate-200";
  if (norm === "sentencia_favorable") return "bg-emerald-50 text-emerald-900 border-emerald-200";
  return "bg-slate-100 text-slate-700 border-slate-200";
}

/** Proveedor o cliente del contribuyente auditado (según dirección del CFDI). */
function rolContraparteCorto(cfdi) {
  const rol = (cfdi?.rol_contraparte || "").toLowerCase();
  if (rol.includes("proveedor")) return "Proveedor";
  if (rol.includes("cliente")) return "Cliente";
  return (cfdi?.direccion || "").toLowerCase() === "recibido" ? "Proveedor" : "Cliente";
}

export default function AuditoriaEfos() {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = usePersistedClientId(clients);
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [soloRiesgo, setSoloRiesgo] = useState(true);
  const [status, setStatus] = useState(null);
  const [audit, setAudit] = useState(null);
  const [busySync, setBusySync] = useState(false);
  const [busyAudit, setBusyAudit] = useState(false);
  const [busyUpload, setBusyUpload] = useState(false);
  const [lookupRfc, setLookupRfc] = useState("");
  const [lookupResult, setLookupResult] = useState(null);
  const [busyLookup, setBusyLookup] = useState(false);
  const [weeklyLast, setWeeklyLast] = useState(null);

  const loadStatus = useCallback(() => {
    api.get("/efos/status").then(({ data }) => setStatus(data)).catch(() => setStatus(null));
    api.get("/efos/weekly-last-run").then(({ data }) => setWeeklyLast(data)).catch(() => setWeeklyLast(null));
  }, []);

  useEffect(() => {
    api.get("/clients").then(({ data }) => setClients(data));
    loadStatus();
  }, [loadStatus]);

  const onSync = async () => {
    setBusySync(true);
    try {
      const { data } = await api.post("/efos/sync", null, { timeout: 180000 });
      toast.success(`Lista 69-B actualizada: ${data.total?.toLocaleString()} contribuyentes`);
      loadStatus();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo descargar la lista del SAT");
    } finally {
      setBusySync(false);
    }
  };

  const onUploadCsv = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setBusyUpload(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/efos/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 180000,
      });
      toast.success(`Archivo cargado: ${data.total?.toLocaleString()} RFC en lista`);
      loadStatus();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "No se pudo importar el archivo");
    } finally {
      setBusyUpload(false);
    }
  };

  const consultarRfc = async () => {
    const query = normalizeConsultaEfosQuery(lookupRfc);
    if (query.length < 3) {
      toast.error("Indique un RFC (12–13 caracteres) o al menos 3 letras del nombre de la empresa");
      return;
    }
    const compact = normalizeConsultaRfc(query);
    const looksRfc = /^[A-ZÑ&]{3,4}\d{6}[A-Z0-9]{3}$/.test(compact);
    if (looksRfc && compact.length < 12) {
      toast.error("Indique un RFC válido (12 o 13 caracteres)");
      return;
    }
    setBusyLookup(true);
    setLookupResult(null);
    try {
      const { data } = await api.get("/efos/lookup", { params: { q: query } });
      setLookupResult(data);
      const seccion = data.seccion_lista || SITUACION_SECCION[data.situacion_norm] || data.situacion_label;
      if (data.found && data.es_riesgo) {
        toast.warning(
          `${data.rfc || query} aparece en 69-B · ${seccion || data.situacion_label || data.situacion}`,
        );
      } else if (data.found) {
        toast.message(
          `${data.rfc || query} está en el listado · ${seccion || data.situacion_label || data.situacion}`,
        );
      } else {
        toast.success(
          looksRfc
            ? `${compact} no aparece en Presuntos, Definitivos ni Desvirtuados`
            : `Sin coincidencias por nombre en la lista 69-B vigente`,
        );
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo consultar el RFC o la empresa");
    } finally {
      setBusyLookup(false);
    }
  };

  const runAudit = async () => {
    if (!clientId) {
      toast.error("Seleccione un cliente");
      return;
    }
    setBusyAudit(true);
    setAudit(null);
    try {
      const { data } = await api.get("/efos/audit", {
        params: {
          client_id: clientId,
          year: year ? Number(year) : undefined,
          solo_riesgo: soloRiesgo,
        },
        timeout: 120000,
      });
      setAudit(data);
      if (data.hallazgos === 0) {
        toast.success("Sin contrapartes de sus CFDI en la lista 69-B (con el filtro aplicado)");
      } else {
        toast.warning(`${data.hallazgos} RFC en lista · ${data.cfdi_en_lista} CFDI relacionados`);
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Error al auditar");
    } finally {
      setBusyAudit(false);
    }
  };

  const openVisor = async (uuid) => {
    try {
      await goToSatVisorForUuid(navigate, clientId, uuid);
    } catch {
      toast.error("No se pudo abrir el CFDI en el visor");
    }
  };

  const listaFecha = status?.updated_at
    ? new Date(status.updated_at).toLocaleString("es-MX", { dateStyle: "medium", timeStyle: "short" })
    : null;
  const weeklyAuto = status?.weekly_auto;
  const weeklyCheckAt = weeklyAuto?.last_check_at
    ? new Date(weeklyAuto.last_check_at).toLocaleString("es-MX", { dateStyle: "medium", timeStyle: "short" })
    : null;
  const weeklyRunAt = weeklyAuto?.last_run_at
    ? new Date(weeklyAuto.last_run_at).toLocaleString("es-MX", { dateStyle: "medium", timeStyle: "short" })
    : null;
  const weeklySummary = weeklyLast?.findings_summary || [];

  return (
    <>
      <PageHeader
        title="Auditoría de CFDI (EFOS)"
        subtitle="Cruce de sus XML cargados contra la lista 69-B del SAT (operaciones presuntamente inexistentes)."
        breadcrumb={[VISOR_MODULE_BREADCRUMB_ROOT, "Auditoría EFOS"]}
      />
      <div className="p-8 max-w-7xl mx-auto space-y-6">
        <div
          className="rounded-sm border border-amber-200 bg-amber-50/90 px-4 py-3 text-sm text-slate-800 flex gap-3"
          role="note"
        >
          <Info className="w-5 h-5 shrink-0 text-amber-800 mt-0.5" />
          <div className="space-y-1 text-[12px] leading-relaxed">
            <p>
              La lista 69-B se verifica <strong>automáticamente cada {weeklyAuto?.interval_days || 15} días</strong>{" "}
              (worker <code className="text-[10px]">efos_weekly_scheduler</code>). Si el SAT publicó cambios, se
              auditan todos los clientes del despacho; si la lista no cambió, se conservan los hallazgos anteriores sin
              recargar el sistema. Con contrapartes en riesgo se genera una <strong>alerta interna</strong> para el
              titular (módulo Despacho → alertas / BNT Asistente). También puede actualizar manualmente abajo.
            </p>
            {(weeklyCheckAt || weeklyRunAt) && (
              <p className="text-[11px] text-slate-700">
                {weeklyCheckAt && (
                  <>
                    Última verificación automática: <strong>{weeklyCheckAt}</strong>
                  </>
                )}
                {weeklyRunAt && weeklyAuto?.last_status === "completed" && (
                  <>
                    {weeklyCheckAt ? " · " : ""}
                    Última auditoría con cambios: <strong>{weeklyRunAt}</strong>
                  </>
                )}
                {weeklyAuto?.last_period_key ? ` (${weeklyAuto.last_period_key})` : ""}
                {weeklyAuto?.last_status === "no_changes" ? (
                  <> · lista sin cambios (se conservan hallazgos previos)</>
                ) : typeof weeklyAuto?.last_total_findings === "number" && weeklyAuto.last_total_findings > 0 ? (
                  <> · <strong className="text-amber-900">{weeklyAuto.last_total_findings} hallazgo(s)</strong></>
                ) : weeklyRunAt ? (
                  <> · sin hallazgos en riesgo</>
                ) : null}
              </p>
            )}
            {status?.sat_csv_url && (
              <p className="text-[11px] text-slate-600">
                Fuente:{" "}
                <a href={status.sat_csv_url} target="_blank" rel="noreferrer" className="underline text-[#0F2D5C]">
                  Listado completo 69-B (SAT)
                </a>
              </p>
            )}
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-sm p-5 space-y-4">
          <h3 className="text-sm font-bold text-[#0F2D5C] flex items-center gap-2">
            <RefreshCw className="w-4 h-4" /> 1. Lista 69-B en el sistema
          </h3>
          <p className="text-xs text-slate-600">
            {listaFecha ? (
              <>
                Última actualización: <strong>{listaFecha}</strong> ·{" "}
                {status?.total?.toLocaleString()} RFC ·{" "}
                {status?.riesgo_count?.toLocaleString()} en riesgo (presunto/definitivo)
              </>
            ) : (
              <span className="text-amber-800 font-medium">Aún no hay lista cargada. Pulse actualizar desde SAT.</span>
            )}
          </p>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              onClick={onSync}
              disabled={busySync}
              className="bg-[#0F2D5C] hover:bg-[#0b2249] h-9 text-xs rounded-sm"
              data-testid="efos-sync-btn"
            >
              {busySync ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1.5" />}
              Actualizar desde SAT
            </Button>
            <label className="inline-flex items-center justify-center gap-1.5 h-9 px-3 text-xs rounded-sm border border-slate-300 bg-white hover:bg-slate-50 cursor-pointer text-slate-800">
              {busyUpload ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              Subir CSV del SAT
              <input type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={onUploadCsv} disabled={busyUpload} />
            </label>
          </div>
        </div>

        {weeklySummary.length > 0 && (
          <div
            className="bg-white border border-slate-200 rounded-sm p-5 space-y-3"
            data-testid="efos-weekly-summary"
          >
            <h3 className="text-sm font-bold text-[#0F2D5C] flex items-center gap-2">
              <ShieldAlert className="w-4 h-4" /> Hallazgos de la última auditoría automática
            </h3>
            <p className="text-[11px] text-slate-600">
              Relación de la última auditoría automática con cambios en la lista (cada{" "}
              {weeklyAuto?.interval_days || 15} días como máximo).
            </p>
            <div className="overflow-x-auto border border-slate-100 rounded-sm">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-[10px] uppercase text-slate-500">
                  <tr>
                    <th className="text-left px-3 py-2">Empresa auditada</th>
                    <th className="text-left px-3 py-2">Rol</th>
                    <th className="text-left px-3 py-2">Contraparte</th>
                    <th className="text-left px-3 py-2">Situación</th>
                    <th className="text-left px-3 py-2">Publicación</th>
                    <th className="text-left px-3 py-2">CFDI</th>
                    <th className="text-left px-3 py-2">Fecha</th>
                  </tr>
                </thead>
                <tbody>
                  {weeklySummary.map((row, idx) => (
                    <tr key={`${row.cfdi_uuid || row.contraparte_rfc}-${idx}`} className="border-t border-slate-100">
                      <td className="px-3 py-2">{row.client_razon_social || "—"}</td>
                      <td className="px-3 py-2">{row.rol || "—"}</td>
                      <td className="px-3 py-2">
                        <span className="font-mono text-[10px]">{row.contraparte_rfc}</span>
                        {row.contraparte_nombre ? (
                          <span className="block text-[10px] text-slate-500">{row.contraparte_nombre}</span>
                        ) : null}
                      </td>
                      <td className="px-3 py-2">
                        <span
                          className={`text-[10px] font-semibold uppercase px-2 py-0.5 rounded-sm border ${situacionBadgeClass(row.situacion_norm)}`}
                        >
                          {row.situacion || SITUACION_LABEL[row.situacion_norm] || "—"}
                        </span>
                      </td>
                      <td className="px-3 py-2">{row.fecha_publicacion || "—"}</td>
                      <td className="px-3 py-2 font-mono text-[10px]">{row.cfdi_uuid || "—"}</td>
                      <td className="px-3 py-2">{row.cfdi_fecha || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div className="bg-white border border-slate-200 rounded-sm p-5 space-y-4">
          <h3 className="text-sm font-bold text-[#0F2D5C] flex items-center gap-2">
            <Search className="w-4 h-4" /> 2. Consultar RFC o empresa
          </h3>
          <p className="text-xs text-slate-600">
            Verifique si un proveedor o cliente está en la lista 69-B vigente (apartados{" "}
            <strong>Presuntos</strong>, <strong>Definitivos</strong> o <strong>Desvirtuados</strong>). Puede
            buscar por RFC o por nombre / razón social. No requiere elegir empresa ni auditar todo el ejercicio.
          </p>
          <div className="flex flex-wrap items-end gap-3">
            <div className="min-w-[16rem] flex-1 max-w-xl">
              <Label htmlFor="efos-rfc-lookup" className="text-xs">
                RFC o nombre de empresa
              </Label>
              <Input
                id="efos-rfc-lookup"
                data-testid="efos-rfc-lookup-input"
                value={lookupRfc}
                onChange={(e) => setLookupRfc(normalizeConsultaEfosQuery(e.target.value))}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    consultarRfc();
                  }
                }}
                placeholder="XAXX010101000 o razón social"
                maxLength={200}
                autoComplete="off"
                spellCheck={false}
                className="w-full h-9 mt-1 rounded-sm"
              />
            </div>
            <Button
              type="button"
              onClick={consultarRfc}
              disabled={busyLookup || normalizeConsultaEfosQuery(lookupRfc).length < 3}
              className="h-9 rounded-sm bg-[#0F2D5C] hover:bg-[#0b2249] text-xs"
              data-testid="efos-rfc-lookup-btn"
            >
              {busyLookup ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Search className="w-4 h-4 mr-1.5" />}
              Verificar en 69-B
            </Button>
          </div>
          {lookupResult && (
            <div
              data-testid="efos-rfc-lookup-result"
              className={`rounded-sm border px-3 py-3 text-xs flex gap-2 items-start ${
                lookupResult.found && lookupResult.es_riesgo
                  ? "border-red-300 bg-red-50 text-red-950"
                  : lookupResult.found
                    ? "border-amber-200 bg-amber-50 text-amber-950"
                    : "border-emerald-200 bg-emerald-50 text-emerald-950"
              }`}
            >
              {lookupResult.found && lookupResult.es_riesgo ? (
                <XCircle className="w-4 h-4 shrink-0 mt-0.5" />
              ) : lookupResult.found ? (
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              ) : (
                <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />
              )}
              <div className="space-y-2 w-full min-w-0">
                {lookupResult.found ? (
                  <p>
                    {lookupResult.query_mode === "nombre" && (lookupResult.matches_count || 1) > 1 ? (
                      <>
                        Se encontraron <strong>{lookupResult.matches_count}</strong> coincidencias por nombre en la
                        lista 69-B.
                      </>
                    ) : (
                      <>
                        El RFC <strong className="font-mono">{lookupResult.rfc}</strong>
                        {lookupResult.nombre ? (
                          <>
                            {" "}
                            ({lookupResult.nombre})
                          </>
                        ) : null}{" "}
                        <strong>sí aparece</strong> en la lista 69-B en el apartado{" "}
                        <span
                          className={`text-[10px] font-semibold uppercase px-2 py-0.5 rounded-sm border ${situacionBadgeClass(lookupResult.situacion_norm)}`}
                          data-testid="efos-rfc-lookup-seccion"
                        >
                          {seccionListaLabel(lookupResult)}
                        </span>
                        {lookupResult.fecha_publicacion ? (
                          <>
                            {" "}
                            · Publicado: <strong>{lookupResult.fecha_publicacion}</strong>
                          </>
                        ) : null}
                        . Consulte con su asesor fiscal.
                      </>
                    )}
                  </p>
                ) : (
                  <p>
                    {lookupResult.query_mode === "nombre" ? (
                      <>
                        No se encontró la empresa «{lookupResult.query}» en Presuntos, Definitivos ni Desvirtuados
                        de la lista 69-B vigente.
                      </>
                    ) : (
                      <>
                        El RFC <strong className="font-mono">{lookupResult.rfc}</strong>{" "}
                        <strong>no aparece</strong> en Presuntos, Definitivos ni Desvirtuados de la lista 69-B
                        vigente del sistema.
                      </>
                    )}
                  </p>
                )}
                {lookupResult.found && Array.isArray(lookupResult.matches) && lookupResult.matches.length > 1 ? (
                  <ul className="space-y-1.5 border-t border-current/10 pt-2" data-testid="efos-rfc-lookup-matches">
                    {lookupResult.matches.map((m) => (
                      <li key={m.rfc} className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                        <span className="font-mono font-semibold">{m.rfc}</span>
                        <span className="text-slate-700">{m.nombre || "—"}</span>
                        <span
                          className={`text-[10px] font-semibold uppercase px-2 py-0.5 rounded-sm border ${situacionBadgeClass(m.situacion_norm)}`}
                        >
                          {m.seccion_lista || SITUACION_SECCION[m.situacion_norm] || m.situacion_label || m.situacion}
                        </span>
                        {m.fecha_publicacion ? (
                          <span className="text-[10px] text-slate-600">Pub. {m.fecha_publicacion}</span>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                ) : null}
                <p className="text-[11px] text-slate-600" data-testid="efos-rfc-lookup-aclaracion">
                  {lookupResult.aclaracion || lookupResult.nota}
                </p>
              </div>
            </div>
          )}
        </div>

        <div className="bg-white border border-slate-200 rounded-sm p-5 space-y-4">
          <h3 className="text-sm font-bold text-[#0F2D5C] flex items-center gap-2">
            <ShieldAlert className="w-4 h-4" /> 3. Auditar cliente
          </h3>
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <Label className="text-xs">Cliente</Label>
              <Select value={clientId} onValueChange={setClientId}>
                <SelectTrigger className="w-64 h-9 mt-1 rounded-sm" data-testid="efos-client-select">
                  <SelectValue placeholder="Seleccionar cliente" />
                </SelectTrigger>
                <SelectContent>
                  {clients.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.razon_social} ({c.rfc})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Ejercicio</Label>
              <Select value={year} onValueChange={setYear}>
                <SelectTrigger className="w-28 h-9 mt-1 rounded-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[0, 1, 2, 3, 4].map((i) => {
                    const y = new Date().getFullYear() - i;
                    return (
                      <SelectItem key={y} value={String(y)}>
                        {y}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2 pb-1">
              <Checkbox id="solo-riesgo" checked={soloRiesgo} onCheckedChange={(v) => setSoloRiesgo(Boolean(v))} />
              <Label htmlFor="solo-riesgo" className="text-xs cursor-pointer">
                Solo presuntos y definitivos
              </Label>
            </div>
            <Button
              onClick={runAudit}
              disabled={busyAudit || !clientId}
              className="h-9 rounded-sm bg-slate-900 hover:bg-slate-800 text-xs"
              data-testid="efos-run-audit-btn"
            >
              {busyAudit ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <ShieldAlert className="w-4 h-4 mr-1.5" />}
              Ejecutar auditoría
            </Button>
          </div>

          {audit?.client_on_list && (
            <div className="rounded-sm border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-950 flex gap-2 items-start">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              <div className="space-y-1">
                <p>
                  El RFC del cliente <strong className="font-mono">{audit.client_rfc}</strong> aparece en la lista
                  69-B en el apartado{" "}
                  <strong>
                    {audit.client_on_list.seccion_lista ||
                      SITUACION_SECCION[audit.client_on_list.situacion_norm] ||
                      SITUACION_LABEL[audit.client_on_list.situacion_norm] ||
                      audit.client_on_list.situacion}
                  </strong>
                  {audit.client_on_list.fecha_publicacion ? (
                    <>
                      {" "}
                      · Publicado: <strong>{audit.client_on_list.fecha_publicacion}</strong>
                    </>
                  ) : null}
                  . Consulte con su asesor fiscal.
                </p>
                <p className="text-[11px] text-red-900/80">
                  Aclaración: el RFC de la empresa auditada figura en «
                  {audit.client_on_list.seccion_lista ||
                    SITUACION_SECCION[audit.client_on_list.situacion_norm] ||
                    SITUACION_LABEL[audit.client_on_list.situacion_norm] ||
                    audit.client_on_list.situacion}
                  » del listado 69-B vigente.
                </p>
              </div>
            </div>
          )}

          {audit && (
            <p className="text-[11px] text-slate-600" data-testid="efos-audit-summary">
              {typeof audit.cfdi_emitidos === "number" ? (
                <>
                  CFDI emitidos: <strong>{audit.cfdi_emitidos}</strong> · CFDI recibidos:{" "}
                  <strong>{audit.cfdi_recibidos ?? 0}</strong>
                </>
              ) : (
                <>
                  CFDI revisados: <strong>{audit.cfdi_revisados}</strong>
                </>
              )}{" "}
              · Hallazgos:{" "}
              <strong>{audit.hallazgos}</strong> RFC · CFDI vinculados:{" "}
              <strong>{audit.cfdi_en_lista}</strong>
              {audit.client_razon_social ? (
                <span className="text-slate-500">
                  {" "}
                  (contrapartes como proveedor o cliente de <strong>{audit.client_razon_social}</strong>)
                </span>
              ) : null}
            </p>
          )}
        </div>

        {audit?.findings?.length > 0 && (
          <div className="space-y-4" data-testid="efos-findings">
            {audit.findings.map((f) => (
              <div key={f.rfc} className="bg-white border border-slate-200 rounded-sm overflow-hidden">
                <div className="px-4 py-3 bg-slate-50 border-b border-slate-100 flex flex-wrap items-center gap-2 justify-between">
                  <div>
                    <span className="font-mono font-semibold text-[#0F2D5C]">{f.rfc}</span>
                    <span className="text-xs text-slate-600 ml-2">{f.nombre_lista}</span>
                    {f.is_client_rfc ? (
                      <span className="ml-2 text-[10px] font-semibold uppercase text-red-900 bg-red-100 border border-red-200 rounded-sm px-1.5 py-0.5">
                        RFC del cliente auditado
                      </span>
                    ) : null}
                  </div>
                  <div className="flex flex-wrap items-center gap-2 justify-end">
                    {f.fecha_publicacion ? (
                      <span className="text-[10px] text-slate-600">
                        Publicado: <strong>{f.fecha_publicacion}</strong>
                      </span>
                    ) : null}
                    <span
                      className={`text-[10px] font-semibold uppercase px-2 py-0.5 rounded-sm border ${situacionBadgeClass(f.situacion_norm)}`}
                    >
                      {SITUACION_SECCION[f.situacion_norm] || SITUACION_LABEL[f.situacion_norm] || f.situacion}
                    </span>
                  </div>
                </div>
                <p className="px-4 py-2 text-[10px] text-slate-500">
                  En los CFDI del cliente aparece como: {f.roles?.join(" · ") || "—"}
                  {f.situacion_norm ? (
                    <>
                      {" "}
                      · Apartado 69-B:{" "}
                      <strong>
                        {SITUACION_SECCION[f.situacion_norm] || SITUACION_LABEL[f.situacion_norm] || f.situacion}
                      </strong>
                    </>
                  ) : null}
                </p>
                {f.cfdis?.length ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50 text-[10px] uppercase text-slate-500">
                      <tr>
                        <th className="text-left px-3 py-2">UUID</th>
                        <th className="text-left px-3 py-2">Fecha</th>
                        <th className="text-left px-3 py-2">Tipo</th>
                        <th className="text-left px-3 py-2">Rol</th>
                        <th className="text-right px-3 py-2">Total</th>
                        <th className="text-right px-3 py-2">Acc.</th>
                      </tr>
                    </thead>
                    <tbody>
                      {f.cfdis.map((c) => (
                        <tr key={c.uuid} className="border-t border-slate-100">
                          <td className="px-3 py-2 font-mono text-[10px]">{c.uuid}</td>
                          <td className="px-3 py-2">{String(c.fecha || "").slice(0, 10)}</td>
                          <td className="px-3 py-2">{c.tipo_comprobante || "—"}</td>
                          <td className="px-3 py-2">
                            <span
                              className={`inline-block px-1.5 py-0.5 rounded-sm border text-[10px] font-semibold ${
                                rolContraparteCorto(c) === "Proveedor"
                                  ? "bg-violet-50 text-violet-900 border-violet-200"
                                  : "bg-sky-50 text-sky-900 border-sky-200"
                              }`}
                              title={c.rol_contraparte || ""}
                            >
                              {rolContraparteCorto(c)}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-right num">{fmtMoney(c.total)}</td>
                          <td className="px-3 py-2 text-right">
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="h-7 text-[10px]"
                              onClick={() => openVisor(c.uuid)}
                            >
                              <ExternalLink className="w-3 h-3 mr-1" /> Visor
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                ) : (
                  <div className="px-4 py-4 text-xs text-red-900 bg-red-50 border-t border-red-100">
                    El RFC del cliente auditado está en la lista 69-B. No depende de CFDI vinculados; revise la
                    situación directamente con el SAT y su asesor fiscal.
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {audit && audit.findings?.length === 0 && (
          <div className="text-center py-12 text-sm text-slate-500 border border-dashed border-slate-200 rounded-sm bg-white">
            No se encontraron CFDI del cliente con contrapartes en la lista (filtro actual).
          </div>
        )}
      </div>
    </>
  );
}
